import { createFileRoute, Link } from "@tanstack/react-router";
import { openCookiePreferences } from "@shared/lib/cookieConsent";
import {
  ContentPage,
  ContentSection,
  ContentTable,
} from "../components/content/ContentPage";

// Operatorul confirmat de administrator. Adresa sediului se completează aici
// când e disponibilă — deocamdată identificarea se face prin denumire și CUI.
const COMPANY = "FRUIT CREATIVE S.R.L.";
const CUI = "RO39066744";
const CONTACT_EMAIL = "contact@suuper.ro";
const LAST_UPDATED = "12 august 2026";

export const Route = createFileRoute("/confidentialitate")({
  head: () => ({
    meta: [
      { title: "Politica de confidențialitate — suuper" },
      {
        name: "description",
        content:
          "Ce date colectează suuper, de ce, cât timp le păstrăm și cum îți exerciți drepturile GDPR.",
      },
      { name: "robots", content: "index, follow" },
    ],
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <ContentPage
      title="Politica de confidențialitate"
      eyebrow="DOCUMENT LEGAL"
      meta={`Ultima actualizare: ${LAST_UPDATED}`}
      intro={
        <>
          Pe scurt: dacă te înscrii pe listă, îți cerem doar adresa de email —
          orașul și statutul de student sunt opționale. Nu te urmărim între
          site-uri, nu folosim amprentare de dispozitiv pe formularele publice
          și nu vindem datele nimănui. Analitica pornește doar dacă o accepți.
        </>
      }
    >
      <ContentSection id="operator" title="1. Cine suntem">
        <p>
          Platforma suuper este operată de <strong>{COMPANY}</strong>, CUI{" "}
          {CUI}, în calitate de operator de date cu caracter personal, conform
          Regulamentului (UE) 2016/679 (GDPR).
        </p>
        <p>
          Pentru orice chestiune legată de datele tale, scrie-ne la{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="font-medium text-primary hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </ContentSection>

      <ContentSection id="date" title="2. Ce date colectăm și de ce">
        <p>
          Colectăm diferit, în funcție de ce faci pe site. Mai jos e lista
          completă, pe fiecare situație.
        </p>

        <h3 className="pt-2 text-lg font-bold text-text">
          2.1. Înscrierea pe lista de așteptare
        </h3>
        <ContentTable
          head={["Dată", "Obligatorie", "De ce"]}
          rows={[
            ["Adresa de email", "Da", "Ca să te anunțăm la lansare și la deblocarea unui preț"],
            ["Orașul", "Nu", "Ca să știm unde să începem"],
            ["Statutul de student", "Nu", "Ca să-ți arătăm ofertele dedicate"],
            [
              "Cod de invitație",
              "Nu",
              "Ca să știm cine te-a invitat, dacă ai venit printr-un link",
            ],
            [
              "Pagina de unde te-ai înscris",
              "Automat",
              "Ca să vedem ce secțiune convinge",
            ],
          ]}
        />
        <p>
          <strong>Temei legal:</strong> consimțământul tău, exprimat prin
          trimiterea formularului. Îl poți retrage oricând, din linkul de
          dezabonare aflat în fiecare email.
        </p>
        <p>
          Nu colectăm amprentă de dispozitiv pe acest formular. Ca să nu fie
          abuzat, limităm numărul de emailuri trimise pe oră la nivelul întregului
          site — o măsură care nu identifică pe nimeni.
        </p>

        <h3 className="pt-2 text-lg font-bold text-text">
          2.2. Cererile de parteneriat
        </h3>
        <p>
          Dacă trimiți formularul pentru business, colectăm numele brandului,
          persoana de contact, emailul și, opțional, telefonul, site-ul,
          categoria și mesajul tău.
        </p>
        <p>
          <strong>Temei legal:</strong> demersuri precontractuale, la cererea ta.
        </p>

        <h3 className="pt-2 text-lg font-bold text-text">
          2.3. Contul din aplicație
        </h3>
        <p>
          Când îți faci cont, prelucrăm adresa de email, numărul de telefon și
          starea abonamentului. Pentru verificarea numărului trimitem un cod prin
          SMS și reținem temporar adresa IP de la care s-a cerut, ca protecție
          împotriva abuzului.
        </p>
        <p>
          Pentru prevenirea fraudei reținem și o amprentă a dispozitivelor de pe
          care te autentifici, împreună cu tipul de browser. Această prelucrare
          se aplică <em>numai</em> conturilor din aplicație, nu vizitatorilor
          site-ului public.
        </p>
        <p>
          <strong>Temei legal:</strong> executarea contractului pentru
          funcționarea serviciului; interesul legitim de a preveni frauda pentru
          amprentă și adresa IP.
        </p>

        <h3 className="pt-2 text-lg font-bold text-text">
          2.4. Folosirea unei oferte la partener
        </h3>
        <p>
          Când validezi o ofertă într-o locație, înregistrăm oferta, partenerul,
          momentul și <strong>coordonatele geografice ale validării</strong>.
          Locația confirmă că validarea s-a produs efectiv acolo și previne
          folosirea codurilor de la distanță.
        </p>
        <p>
          <strong>Temei legal:</strong> executarea contractului și interesul
          legitim de a preveni frauda. Poți refuza accesul la locație din
          setările telefonului; unele validări nu vor fi posibile.
        </p>

        <h3 className="pt-2 text-lg font-bold text-text">2.5. Plăți</h3>
        <p>
          Plățile sunt procesate de Stripe. Noi nu vedem și nu stocăm datele
          cardului tău — reținem doar identificatorii de client și de abonament
          primiți de la Stripe.
        </p>
      </ContentSection>

      <ContentSection id="cookies" title="3. Cookie-uri și analitică">
        <p>
          Folosim cookie-uri strict necesare, care păstrează sesiunea, preferința
          de limbă și chiar alegerea ta privind cookie-urile. Fără ele site-ul nu
          funcționează, deci nu pot fi dezactivate.
        </p>
        <p>
          Separat, folosim <strong>PostHog</strong> pentru a înțelege cum e
          folosit site-ul. Acesta pornește <strong>numai</strong> dacă îl accepți:
          până atunci scriptul nu se încarcă deloc. Îți poți schimba alegerea
          oricând.
        </p>
        <p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="font-medium text-primary hover:underline"
          >
            Deschide setările de cookie-uri
          </button>
        </p>
      </ContentSection>

      <ContentSection id="terti" title="4. Cui transmitem datele">
        <p>
          Nu vindem și nu închiriem datele nimănui. Le transmitem doar
          furnizorilor de care avem nevoie ca să funcționeze serviciul, fiecare
          obligat contractual să le protejeze:
        </p>
        <ContentTable
          head={["Furnizor", "Rol", "Unde"]}
          rows={[
            ["Convex", "Baza de date și funcțiile de backend", "SUA"],
            ["Vercel", "Găzduirea site-ului", "SUA"],
            ["Resend", "Trimiterea emailurilor", "SUA"],
            ["Twilio", "Trimiterea SMS-urilor de verificare", "SUA"],
            ["Stripe", "Procesarea plăților", "SUA / UE"],
            ["PostHog", "Analitică, doar cu acordul tău", "UE"],
          ]}
        />
        <p>
          Transferurile către furnizorii din afara Spațiului Economic European se
          fac pe baza clauzelor contractuale standard adoptate de Comisia
          Europeană.
        </p>
      </ContentSection>

      <ContentSection id="durata" title="5. Cât păstrăm datele">
        <p>
          Datele de pe lista de așteptare le păstrăm până la lansare sau până când
          te dezabonezi. După dezabonare reținem doar adresa marcată ca
          dezabonată, ca să nu-ți mai scriem din greșeală.
        </p>
        <p>
          Datele de cont se păstrează cât timp contul este activ. După ștergerea
          contului, le eliminăm în maximum 30 de zile, cu excepția documentelor
          fiscale, pe care legea ne obligă să le păstrăm 10 ani.
        </p>
        <p>
          Cererile de parteneriat se păstrează cât durează discuția comercială și
          maximum 2 ani după, dacă nu se concretizează.
        </p>
      </ContentSection>

      <ContentSection id="drepturi" title="6. Drepturile tale">
        <p>Conform GDPR, ai dreptul să:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>ceri o copie a datelor pe care le avem despre tine;</li>
          <li>corectezi datele inexacte;</li>
          <li>ceri ștergerea lor;</li>
          <li>restricționezi sau te opui unei prelucrări;</li>
          <li>primești datele într-un format structurat, ca să le poți muta;</li>
          <li>
            retragi consimțământul oricând, fără să afectezi prelucrarea de
            dinainte.
          </li>
        </ul>
        <p>
          Scrie-ne la{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="font-medium text-primary hover:underline"
          >
            {CONTACT_EMAIL}
          </a>{" "}
          și îți răspundem în cel mult 30 de zile.
        </p>
        <p>
          Dacă nu ești mulțumit de răspuns, te poți adresa Autorității Naționale
          de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP),{" "}
          <a
            href="https://www.dataprotection.ro"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary hover:underline"
          >
            dataprotection.ro
          </a>
          .
        </p>
      </ContentSection>

      <ContentSection id="securitate" title="7. Securitate">
        <p>
          Traficul este criptat în tranzit (HTTPS). Accesul la datele
          administrative este limitat pe roluri, iar acțiunile administratorilor
          sunt înregistrate. Nu stocăm parole în clar și nu avem acces la datele
          cardurilor.
        </p>
      </ContentSection>

      <ContentSection id="minori" title="8. Minori">
        <p>
          suuper se adresează persoanelor de peste 16 ani. Nu colectăm cu bună
          știință date despre copii sub această vârstă. Dacă afli că un minor
          ne-a transmis date, scrie-ne și le ștergem.
        </p>
      </ContentSection>

      <ContentSection id="modificari" title="9. Modificări">
        <p>
          Putem actualiza această politică. Dacă schimbările sunt semnificative,
          te anunțăm prin email sau printr-un mesaj pe site înainte să intre în
          vigoare. Data ultimei actualizări este afișată la începutul paginii.
        </p>
        <p>
          Vezi și{" "}
          <Link
            to="/termeni"
            className="font-medium text-primary hover:underline"
          >
            termenii și condițiile
          </Link>
          .
        </p>
      </ContentSection>
    </ContentPage>
  );
}
